import {
  CreateNewFolderOutlined,
  FolderOpenOutlined,
  UploadFileOutlined,
} from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useDownloadAttachmentMutation,
  useDownloadLibraryZipMutation,
} from '../../../../app/athenaApi';
import {
  HandlersCreateLibraryDirectoryRequest,
  HandlersLibraryTreeNode,
  useGetApiAttachmentsByIdQuery,
  useGetApiDeliverablesByIdQuery,
  useGetApiLibraryDirectoriesByDirectoryIdQuery,
  useGetApiLibraryTreeQuery,
  usePostApiLibraryDirectoriesMutation,
} from '../../../../app/athenaApiGen';
import {
  setInfoBarDetail,
  setPendingCreateItemId,
  setPendingEditItemId,
  setSelectedItem,
  toggleModal,
} from '../../../../app/library/librarySlice';
import { RootState } from '../../../../app/store';
import { isFormTypeAvailable } from '../../../utilities';
import { streamZipToDisk } from '../../../../utils/streamZipToDisk';
import { formViewRouteByAbbreviation } from '../../../formSchemaRegistry';
import {
  DetailsContainer,
  ViewDetailsContainer,
  ViewHeaderContainer,
  ViewPanelContainer,
} from '../../StyledComponents';
import { getEntityIcon } from '../entityIcon';
import NoPermissionText from '../NoPermissionText';
import EmptyView from './components/EmptyView';
import { hasDownloadableFile } from '../../consts';

// Enable UTC plugin
dayjs.extend(utc);
dayjs.extend(relativeTime);

type TViewPanelProps = {
  treeRef: React.RefObject<any>;
};

const ViewPanel = (props: TViewPanelProps) => {
  const dispatch = useDispatch();
  const { selectedItem, isItemLoading, allowedActions } = useSelector(
    (state: RootState) => state.library,
  );

  const isViewItemEnabled = !!allowedActions.find(({ action }) =>
    action?.includes('get-directory'),
  );
  // Permission flags for the container empty-state CTAs.
  const canCreateFolder = !!allowedActions.find(({ action }) =>
    action?.includes('create-directory'),
  );
  const canUploadFile = !!allowedActions.find(({ action }) =>
    action?.includes('update-directory'),
  );

  // Mirrors the ActionMenu Download path — same mutation, same permission key,
  // same entityId. The button only renders for attachments; directories /
  // programs / projects don't have a downloadable file.
  const [download, { isLoading: isDownloading }] =
    useDownloadAttachmentMutation();
  const [downloadLibraryZip] = useDownloadLibraryZipMutation();
  const isAttachment = !!selectedItem?.entityType?.includes('attachment');
  const canDownload =
    isAttachment && isViewItemEnabled && !!selectedItem?.entityId;

  // Mirror the deliverables page ViewPanel: when the selected library item is
  // a deliverable, fetch its full payload to surface fields the tree node
  // doesn't carry (dueDate, author, deliverableType.abbreviation, workflowState).
  const navigate = useNavigate();
  const isDeliverable = selectedItem?.entityType === 'deliverable';
  const { data: deliverableData } = useGetApiDeliverablesByIdQuery(
    { id: selectedItem?.entityId ?? '' },
    { skip: !isDeliverable || !selectedItem?.entityId },
  );
  const availableForms = useSelector(
    (state: RootState) => state.user.availableForms,
  );
  const token = useSelector((state: RootState) => state.user.loginToken);
  // Covers the whole "Download all as zip" flow (streaming + blob fallback) so
  // the button shows a spinner on both paths.
  const [isPreparingZip, setIsPreparingZip] = React.useState(false);
  // Pull the abbreviation from the tree node first (synchronous, no async
  // fetch). The per-item deliverableData fetch is only a fallback in case
  // the tree node didn't carry deliverableType (older payloads). This lets
  // the View Form link render immediately on click instead of popping in
  // after the deliverableData query resolves, which was causing the right
  // pane to visibly shift.
  const deliverableAbbreviation = (
    selectedItem?.deliverableType?.abbreviation ??
    deliverableData?.deliverableType?.abbreviation ??
    ''
  ).toLowerCase();
  const deliverableIdForLink =
    selectedItem?.entityId ?? deliverableData?.deliverableId;
  const isForm = ['ctp', 'pnr', 'rip', 'tar', 'tr'].includes(
    deliverableAbbreviation,
  );
  const canViewForm =
    isForm && isFormTypeAvailable(availableForms, deliverableAbbreviation);
  // Note: the deliverables page ViewPanel exposes an "Open in Approval Queue"
  // link here. We intentionally don't surface it in the library view — the
  // library is a browse surface, and bouncing the user to a different page
  // mid-browse is a context break, not a feature. Approvers go to the
  // Approval Queue page directly. Re-add if a real use case emerges.

  const formatDate = (iso?: string | null) =>
    iso ? dayjs(new Date(iso)).utc().format('DD MMM YYYY') : '-';

  // Container-type entities (program, project, library_directory) don't have
  // meaningful per-item metadata to show — their identity is their children.
  // We drop the Status/Type/dates grid for them and surface either the
  // stats line (when populated) or an empty-state CTA (when empty). Navigation
  // into children happens via the tree on the left, not a child list here.
  const isProgramSel = selectedItem?.entityType === 'program';
  const isProjectSel = selectedItem?.entityType === 'project';
  const isLibraryDir = selectedItem?.entityType === 'library_directory';
  const isContainer = isProgramSel || isProjectSel || isLibraryDir;
  // "Download all as zip" applies to folders, projects (a whole project's
  // files), and deliverables that actually have downloadable files — not
  // programs. A deliverable with nothing to zip (e.g. a form-only TAR, which
  // has no attachments) is excluded by the file-presence check. Same
  // server-built ZIP as the kebab action.
  const canDownloadZip =
    (isLibraryDir ||
      isProjectSel ||
      (isDeliverable && !!selectedItem && hasDownloadableFile(selectedItem))) &&
    isViewItemEnabled &&
    !!selectedItem?.entityId;
  const containerEmpty =
    isContainer && (selectedItem?.children?.length ?? 0) === 0;
  const containerTypeWord = isProgramSel
    ? 'program'
    : isProjectSel
      ? 'project'
      : 'folder';
  // Content gate for "Download all as zip" on folders/projects (which are
  // always shown): recursively walk the already-loaded subtree and disable the
  // button when there's no downloadable file anywhere — not the shallow
  // `containerEmpty` above, which only counts direct children. Deliverables are
  // hidden outright by `canDownloadZip` above when they can't/don't have files,
  // so they never reach this disabled state.
  const noDownloadableFiles =
    (isLibraryDir || isProjectSel) &&
    !!selectedItem &&
    !hasDownloadableFile(selectedItem);

  // The selected directory's own record carries its programId/projectId
  // (program_id is NOT NULL in the schema), so handleAddFolderHere can build a
  // valid create-directory payload for folders nested at any depth. Only needed
  // when the empty-state CTAs are visible; gating on `containerEmpty` avoids a
  // pointless re-render when the user clicks a populated folder.
  const { data: selectedDir } = useGetApiLibraryDirectoriesByDirectoryIdQuery(
    { directoryId: selectedItem?.entityId ?? '' },
    {
      skip: !isLibraryDir || !containerEmpty || !selectedItem?.entityId,
    },
  );
  const [postDirectory] = usePostApiLibraryDirectoriesMutation();

  const handleAddFolderHere = async () => {
    if (!selectedItem) return;
    const payload: HandlersCreateLibraryDirectoryRequest = {
      directoryName: 'Untitled',
      programId: '',
    };
    if (isProgramSel) {
      payload.programId = selectedItem.entityId ?? '';
    } else if (isProjectSel) {
      payload.programId = selectedItem.parentId ?? '';
      payload.projectId = selectedItem.entityId ?? '';
    } else if (isLibraryDir) {
      // Inherit program/project straight off the selected directory's record
      // so creation works for folders nested at any depth.
      payload.parentDirectoryId = selectedItem.entityId ?? '';
      payload.programId = selectedDir?.programId ?? '';
      if (selectedDir?.projectId) {
        payload.projectId = selectedDir.projectId;
      }
    }
    try {
      const result = await postDirectory({
        handlersCreateLibraryDirectoryRequest: payload,
      }).unwrap();
      if (result?.directoryId) {
        dispatch(setPendingEditItemId(result.directoryId));
        // Mark as pending-create so cancel on the rename input rolls
        // the create back (see issue #1327 / CustomTreeItem).
        dispatch(setPendingCreateItemId(result.directoryId));
      }
      // Tree refetch is handled centrally: the create mutation invalidates the
      // 'Library' tag via the enhanceEndpoints override (src/app/athenaApiEnhanced.ts).
    } catch (err) {
      // Surface the backend's validation detail so failures are debuggable
      // from the UI instead of the catch-all string hiding err.data.details.
      const e = err as
        | { data?: { details?: string; message?: string; error?: string } }
        | undefined;
      const detail =
        e?.data?.details ??
        e?.data?.message ??
        e?.data?.error ??
        'Failed to create folder. Please try again.';
      dispatch(setInfoBarDetail(detail));
    }
  };

  const handleAddFileHere = () => {
    dispatch(toggleModal('uploader'));
  };

  // Breadcrumb ancestors — walk the cached library tree to collect the
  // path from root to (but not including) the selected item. Shared cache
  // with DynamicTree's useGetApiLibraryTreeQuery — RTK Query dedupes.
  const { data: libraryTree } = useGetApiLibraryTreeQuery({
    includeAll: false,
  });
  const ancestors = React.useMemo<HandlersLibraryTreeNode[]>(() => {
    if (!libraryTree || !selectedItem?.id) return [];
    const walk = (
      nodes: HandlersLibraryTreeNode[],
      path: HandlersLibraryTreeNode[],
    ): HandlersLibraryTreeNode[] | null => {
      for (const node of nodes) {
        if (node.id === selectedItem.id) return path;
        if (node.children?.length) {
          const r = walk(node.children, [...path, node]);
          if (r) return r;
        }
      }
      return null;
    };
    return walk(libraryTree, []) ?? [];
  }, [libraryTree, selectedItem?.id]);

  // Recursive descendant counts for the container stats line. Only computed
  // for containers; deliverables / attachments have their own bespoke views.
  // Zero counts are dropped at render time so the line stays uncluttered.
  const descendantCounts = React.useMemo(() => {
    if (!isContainer || !selectedItem?.children?.length) return null;
    const counts = {
      project: 0,
      library_directory: 0,
      deliverable: 0,
      attachment: 0,
    };
    const walk = (nodes: HandlersLibraryTreeNode[]) => {
      for (const n of nodes) {
        const t = n.entityType;
        if (t && t in counts) {
          counts[t as keyof typeof counts]++;
        }
        if (n.children?.length) walk(n.children);
      }
    };
    walk(selectedItem.children);
    return counts;
  }, [isContainer, selectedItem]);

  const statsParts: string[] = [];
  if (descendantCounts) {
    const fmt = (n: number, singular: string, plural: string) =>
      `${n.toLocaleString()} ${n === 1 ? singular : plural}`;
    if (descendantCounts.project > 0) {
      statsParts.push(fmt(descendantCounts.project, 'project', 'projects'));
    }
    if (descendantCounts.library_directory > 0) {
      statsParts.push(
        fmt(descendantCounts.library_directory, 'folder', 'folders'),
      );
    }
    if (descendantCounts.deliverable > 0) {
      statsParts.push(
        fmt(descendantCounts.deliverable, 'deliverable', 'deliverables'),
      );
    }
    if (descendantCounts.attachment > 0) {
      statsParts.push(fmt(descendantCounts.attachment, 'file', 'files'));
    }
  }

  const handleCrumbClick = (ancestor: HandlersLibraryTreeNode) => {
    if (!ancestor.id) return;
    // Optimistic select from the cached node (mirrors DynamicTree's
    // findInTree-then-setSelectedItem pattern). Expand the path so the
    // tree visibly reveals the ancestor.
    dispatch(setSelectedItem(ancestor));
    props.treeRef.current?.setItemExpansion?.({
      itemId: ancestor.id,
      shouldBeExpanded: true,
    });
  };

  // Human-friendly entity type word shown under the title, matching the
  // pattern used in the Add File modal subtitle.
  const entityTypeLabel = (() => {
    switch (selectedItem?.entityType) {
      case 'program':
        return 'Program';
      case 'project':
        return 'Project';
      case 'library_directory':
        return 'Folder';
      case 'deliverable':
        return 'Deliverable';
      case 'attachment':
        return 'File';
      default:
        return '';
    }
  })();

  // Compact meta line shown under the title: entity type · file size · relative time.
  // Each segment is conditional; the line is hidden entirely when nothing applies.
  // `updatedAt` is preferred over `createdAt` so users see "freshness" — fall back
  // to created when the entity has never been touched (still informative for new
  // items where they're effectively the same).
  const metaParts: string[] = [];
  if (entityTypeLabel) metaParts.push(entityTypeLabel);
  // File size is intentionally not shown: the API no longer returns a
  // per-attachment fileSize (backend #1620 dropped the per-item GetBlobSize).
  const tsIso = selectedItem?.updatedAt ?? selectedItem?.createdAt;
  if (tsIso) {
    const verb = selectedItem?.updatedAt ? 'Updated' : 'Created';
    metaParts.push(`${verb} ${dayjs(tsIso).fromNow()}`);
  }

  const previewKind = (() => {
    if (!isAttachment) return 'none' as const;
    const mt = selectedItem?.mimeType?.toLowerCase() ?? '';
    const name = selectedItem?.label?.toLowerCase() ?? '';
    // PDF inline preview is intentionally disabled: the signed URL is
    // minted with Content-Disposition: attachment, which Chrome respects
    // inside iframes and forces a download. Users get the Download File
    // button instead. Re-enable the 'pdf' branch once the backend can
    // mint a signed URL without the attachment disposition (e.g. via
    // ?inline=true query param on /api/attachments/{id}/download).
    if (
      mt.startsWith('image/') ||
      /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name)
    )
      return 'image' as const;
    return 'none' as const;
  })();

  // Resolve the preview URL lazily for the selected item only (#1624).
  // Backend #1621 stopped signing a downloadUrl on every library tree node
  // (the dominant library-load cost), so the tree node no longer carries one.
  // Instead, on selecting a previewable attachment we fetch the single-item
  // GET /api/attachments/{id} — which still returns a fresh signed downloadUrl
  // (backend #1620 keeps single-item signing) — and use it exactly as before.
  // The native <img> loads it without a Bearer header because auth is in the
  // SAS token (an iframe/fetch()+Bearer approach fails: CORS-blocked on the
  // Azure blob redirect, and iframes can't send the Authorization header).
  const isPreviewableAttachment =
    isAttachment && previewKind !== 'none' && !!selectedItem?.entityId;
  const { data: previewAttachment, isFetching: isPreviewUrlLoading } =
    useGetApiAttachmentsByIdQuery(
      { id: selectedItem?.entityId ?? '' },
      { skip: !isPreviewableAttachment },
    );
  const previewUrl = isPreviewableAttachment
    ? (previewAttachment?.downloadUrl ?? null)
    : null;

  const handleDownload = async () => {
    if (!selectedItem?.entityId) return;
    try {
      await download(selectedItem.entityId).unwrap();
    } catch {
      // Surfacing the failure is handled by the download mutation's own
      // error pathway upstream; we keep the button responsive here.
    }
  };

  // Download every file under the selected folder/project as a single ZIP,
  // built server-side (GET /api/library/{id}/download). A container with no
  // downloadable files returns 404 → surfaced via the info bar.
  const handleDownloadAllZip = async () => {
    if (!selectedItem?.entityId) return;
    setIsPreparingZip(true);
    try {
      // Primary path: stream the archive straight to disk (Chromium), so large
      // folder/project downloads never buffer the whole ZIP in memory and
      // OOM-crash the tab. Same fix as the attachment bulk download (#1615).
      const streamed = await streamZipToDisk({
        url: `/api/library/${selectedItem.entityId}/download`,
        suggestedName: selectedItem.label || 'download',
        token,
      });
      if (streamed) return;

      // Fallback: buffered blob download for browsers without the File System
      // Access API (Firefox/Safari) — fine for small archives.
      await downloadLibraryZip(selectedItem.entityId).unwrap();
    } catch (err) {
      // User dismissed the "Save As" dialog — not an error.
      if ((err as DOMException)?.name === 'AbortError') return;
      console.error('Download all failed:', err);
      dispatch(
        setInfoBarDetail(
          'Failed to download. This folder may have no downloadable files.',
        ),
      );
    } finally {
      setIsPreparingZip(false);
    }
  };

  return (
    <ViewPanelContainer>
      {!isViewItemEnabled ? (
        <NoPermissionText text={'Viewing Directory is Unavailable.'} />
      ) : isItemLoading ? (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      ) : selectedItem ? (
        <ViewDetailsContainer>
          <Box sx={{ mb: 2 }}>
            <ViewHeaderContainer>
              {ancestors.length > 0 && (
                <Box
                  sx={{
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                  }}
                >
                  <Breadcrumbs
                    maxItems={4}
                    separator="›"
                    sx={{
                      fontSize: 'inherit',
                      color: 'inherit',
                      '& .MuiBreadcrumbs-separator': { mx: 0.75 },
                    }}
                  >
                    {ancestors.map((a) => (
                      <Link
                        key={a.id}
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => handleCrumbClick(a)}
                        sx={{
                          color: 'text.secondary',
                          fontSize: 'inherit',
                          // Kill MUI button-base hover background.
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: 'text.primary',
                          },
                          '&:focus': { backgroundColor: 'transparent' },
                        }}
                      >
                        {a.label}
                      </Link>
                    ))}
                  </Breadcrumbs>
                  <Box component="span" sx={{ mx: 0.75 }}>
                    ›
                  </Box>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getEntityIcon(selectedItem.entityType)}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', lineHeight: 1.2 }}
                    >
                      {selectedItem.label}
                    </Typography>
                    {metaParts.length > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.25 }}
                      >
                        {metaParts.join(' · ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {canDownload && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    sx={{
                      backgroundColor: '#0092CB',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#007AAB',
                      },
                    }}
                  >
                    {isDownloading ? 'Downloading…' : 'Download File'}
                  </Button>
                )}
                {canDownloadZip && (
                  <Tooltip
                    title={
                      noDownloadableFiles
                        ? `This ${containerTypeWord} may have no downloadable files.`
                        : ''
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleDownloadAllZip}
                        disabled={isPreparingZip || noDownloadableFiles}
                        startIcon={
                          isPreparingZip ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: '#fff' }}
                            />
                          ) : undefined
                        }
                        sx={{
                          backgroundColor: '#0092CB',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#007AAB',
                          },
                        }}
                      >
                        {isPreparingZip ? 'Preparing…' : 'Download All'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {isDeliverable && canViewForm && deliverableIdForLink && (
                  <Link
                    underline="hover"
                    variant="body2"
                    onClick={() =>
                      navigate(
                        `/${formViewRouteByAbbreviation[deliverableAbbreviation] ?? deliverableAbbreviation}/${deliverableIdForLink}?mode=view`,
                        { state: { returnTo: '/library' } },
                      )
                    }
                    sx={{
                      color: '#0092CB',
                      fontWeight: 500,
                      cursor: 'pointer',
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View Form
                  </Link>
                )}
              </Box>
              {!isDeliverable && selectedItem['description'] && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      Description:
                    </Box>{' '}
                    {selectedItem['description']}
                  </Typography>
                </Box>
              )}
            </ViewHeaderContainer>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <DetailsContainer>
            {isContainer ? (
              // Container entities (program / project / folder) — no metadata
              // grid; their identity is their children. Empty containers show
              // a CTA; populated ones fall through to the stats line below.
              // The tree on the left is the navigator into children.
              containerEmpty ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    py: 6,
                    color: 'text.secondary',
                  }}
                >
                  <FolderOpenOutlined sx={{ fontSize: 48, opacity: 0.5 }} />
                  <Typography variant="body1">
                    This {containerTypeWord} is empty.
                  </Typography>
                  {canUploadFile && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<UploadFileOutlined />}
                        onClick={handleAddFileHere}
                        sx={{
                          backgroundColor: '#0092CB',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#007AAB',
                          },
                        }}
                      >
                        Add Files
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : null
            ) : isDeliverable ? (
              // View Form link lives in the header row (next to the title),
              // matching where Download File appears for attachments — the
              // primary action belongs above the divider, not in the meta block.
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2">Description:</Typography>
                  <Typography variant="body1">
                    {deliverableData?.description ||
                      selectedItem.description ||
                      '-'}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="subtitle2">Due Date:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {formatDate(deliverableData?.dueDate)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="subtitle2">Date Submitted:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {formatDate(deliverableData?.dateSubmitted)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="subtitle2">Author:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {deliverableData?.author?.name || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ) : null}

            {previewKind !== 'none' && (previewUrl || isPreviewUrlLoading) && (
              <Box
                sx={(theme) => ({
                  mt: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: previewUrl ? undefined : 120,
                  backgroundColor: theme.palette.background.default,
                })}
              >
                {/* PDF inline preview is intentionally disabled — see the
                    previewKind computation above. Currently only images
                    render inline. Restore an `iframe` branch here once the
                    backend supports an inline-disposition signed URL. */}
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={selectedItem.label ?? 'Preview'}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  // Lazy signed-URL fetch in flight — show a spinner rather
                  // than a broken <img> until the URL resolves (#1624).
                  <CircularProgress size={28} />
                )}
              </Box>
            )}
            {isContainer && statsParts.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {statsParts.join(' · ')}
              </Typography>
            )}
          </DetailsContainer>
        </ViewDetailsContainer>
      ) : (
        <EmptyView />
      )}
    </ViewPanelContainer>
  );
};

export { ViewPanel };
